import { Fuel, Zap, Pencil, Trash2 } from 'lucide-react';
import type { Citerne } from '@/types';
import type { CiterneConsommationAgg } from '@/types';
import { Button, Progress } from '@/components/ui';

function formatNum(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : '0';
}

interface CiterneCardProps {
  citerne: Citerne;
  consommation?: CiterneConsommationAgg | null;
  onEdit: (citerne: Citerne) => void;
  onDelete: (citerne: Citerne) => void;
  onJauge: (citerne: Citerne) => void;
  canUpdate?: boolean;
  canDelete?: boolean;
  canCreateJauge?: boolean;
}

export default function CiterneCard({
  citerne,
  consommation,
  onEdit,
  onDelete,
  onJauge,
  canUpdate = true,
  canDelete = true,
  canCreateJauge = true,
}: CiterneCardProps) {
  const capacite = Number(citerne.capacite) || 1;
  const currentValue = Number(citerne.current_value) ?? 0;
  const pct = capacite > 0 ? (currentValue / capacite) * 100 : 0;

  const consommationMoy = consommation
    ? (consommation.consomation_moy_count
        ? consommation.consomation_moy / consommation.consomation_moy_count
        : 0)
    : 0;
  const consommationJ = consommation?.consomation_j ?? 0;
  const nbJoursRest =
    consommationMoy > 0 ? Math.floor(currentValue / consommationMoy) : 0;
  const prixTotal = consommation?.prix ?? 0;
  const virtuale = Boolean(citerne.virtuale);

  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-100 px-4 py-1.5 text-sm font-medium text-teal-800">
          <Fuel className="h-4 w-4" />
          {citerne.code ?? citerne.id}
        </span>
      </div>

      <Progress value={currentValue} max={capacite} className="mb-4" />

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-600">Total :</span>
          <span className="font-medium text-slate-800">{formatNum(currentValue)} L</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-600">Pourcentage :</span>
          <span className="font-medium text-slate-800">{Math.round(pct)} %</span>
        </div>

        {!virtuale && (
          <>
            <div className="flex justify-between">
              <span className="text-slate-600">Consommation / Moy :</span>
              <span className="font-medium text-slate-800">{formatNum(consommationMoy)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Consommation / J :</span>
              <span className="font-medium text-slate-800">{formatNum(consommationJ)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Nb jours rest. :</span>
              <span className="font-medium text-slate-800">{nbJoursRest}</span>
            </div>
          </>
        )}

        {virtuale && (
          <div className="flex justify-between">
            <span className="text-slate-600">Montant total :</span>
            <span className="font-medium text-slate-800">{formatNum(prixTotal)} dh</span>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-1 border-t border-slate-100 pt-3">
        <Button
          variant="ghost"
          size="sm"
          disabled={virtuale || !canCreateJauge}
          onClick={() => onJauge(citerne)}
          aria-label="Jauge"
          title="Jauge"
        >
          <Zap className="h-4 w-4" />
        </Button>
        <Button
          variant="update"
          size="sm"
          disabled={virtuale || !canUpdate}
          onClick={() => onEdit(citerne)}
          aria-label="Modifier"
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={virtuale || !canDelete}
          onClick={() => onDelete(citerne)}
          aria-label="Supprimer"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
